import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import callApi from 'lib/callApi';
import { companyIdAtom } from 'views/state';

import { checkNonZeroArray } from '../../helper';
import { MetricsType, MetricsView } from '../../types';
import { criticalityFiltersAtom, appliedHostIdsAtom } from '../state';

export const isMetricsSectionOpenAtom = atom(false);
export const metricsViewAtom = atomWithStorage(
  'ep_metrics_view',
  MetricsView.Severity,
);
export const timeViewLabels = [
  '0 to 3 Days',
  '3+ to 7 Days',
  '7+ to 14 Days',
  '14+ to 28 Days',
  '28+ Days',
];

export const vulnMetricsDataAtom = atom(async (get) => {
  const isOpen = get(isMetricsSectionOpenAtom);
  if (isOpen) {
    const companyId = get(companyIdAtom);
    const selectedHostIds = get(appliedHostIdsAtom);

    try {
      if (companyId && companyId !== -1) {
        const params = {
          company_id: companyId,
        };
        // Keep large selected-ID lists out of the metrics URL as well.
        const hasSelectedIds = selectedHostIds?.length > 0;
        const requestData = hasSelectedIds
          ? { obj_id_list: selectedHostIds }
          : null;
        const responseData = await callApi(
          'pcm/metrics/',
          hasSelectedIds ? 'POST' : 'GET',
          params,
          requestData,
          null,
          'pcm/metrics/:pc-vuln-metrics',
        );
        return responseData?.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }
  return undefined;
});

export const openVulnMetricsBySeverityAverageDayTotalVulnAtom = atom(
  async (get) => {
    const data: any = await get(vulnMetricsDataAtom);
    const criticalityFilters = get(criticalityFiltersAtom);
    if (data) {
      return getVulnMetrics(
        data.severityBasedData,
        MetricsType.Open,
        'Days',
        criticalityFilters,
      );
    }
    return undefined;
  },
);

export const openVulnMetricsBySeverityCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const criticalityFilters = get(criticalityFiltersAtom);
  if (data) {
    return getVulnMetrics(
      data.severityBasedData,
      MetricsType.Open,
      'Count',
      criticalityFilters,
    );
  }
  return undefined;
});

export const closeVulnMetricsBySeverityAverageDayTotalVulnAtom = atom(
  async (get) => {
    const data: any = await get(vulnMetricsDataAtom);
    const criticalityFilters = get(criticalityFiltersAtom);
    if (data) {
      return getVulnMetrics(
        data.severityBasedData,
        MetricsType.Closed,
        'Days',
        criticalityFilters,
      );
    }
    return undefined;
  },
);

export const closedVulnMetricsBySeverityCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const criticalityFilters = get(criticalityFiltersAtom);
  if (data) {
    return getVulnMetrics(
      data.severityBasedData,
      MetricsType.Closed,
      'Count',
      criticalityFilters,
    );
  }
  return undefined;
});

export const openVulnMetricsByTimeCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const criticalityFilters = get(criticalityFiltersAtom);
  if (data) {
    return getVulnMetricsByTimeData(
      data.timeBasedData,
      MetricsType.Open,
      criticalityFilters,
    );
  }
  return undefined;
});

export const closedVulnMetricsByTimeCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const criticalityFilters = get(criticalityFiltersAtom);
  if (data) {
    return getVulnMetricsByTimeData(
      data.timeBasedData,
      MetricsType.Closed,
      criticalityFilters,
    );
  }
  return undefined;
});

export const openVulnMetricsTooltipAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  if (data) {
    return getVulnMetricsTooltips(
      data.severityBasedData,
      MetricsType.Open,
      'Days',
    );
  }
  return [];
});

export const closedVulnMetricsTooltipAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  if (data) {
    return getVulnMetricsTooltips(
      data.severityBasedData,
      MetricsType.Closed,
      'Days',
    );
  }
  return [];
});

function getVulnMetrics(
  sourceData: any,
  metricsType: MetricsType,
  propertyName: string,
  severityFilter: any,
) {
  const pcNode = sourceData[metricsType];
  const pcValues: any[] = Object.values(pcNode);
  const pcData = pcValues.map((x) => x[propertyName]);
  const resultsArray: number[][] = [];
  if (checkNonZeroArray(pcData)) resultsArray.push(pcData);
  const averageDaysOpenClose = getVulnMetricsAverageDaysOpenClose(
    sourceData,
    metricsType,
    severityFilter,
  );
  const totalVulnsCount = getVulnMetricsTotalVulnsCount(
    sourceData,
    metricsType,
    severityFilter,
  );
  return {
    metricsData: resultsArray,
    averageDaysOpenClose: averageDaysOpenClose,
    totalVulnsCount: totalVulnsCount,
  };
}

function getVulnMetricsTotalVulnsCount(
  sourceData: any,
  metricsType: MetricsType,
  severityFilters: string[],
) {
  const filteredMetricsBySeverity = filterMetricsBySeverity(
    sourceData,
    metricsType,
    severityFilters,
  );

  const validVulnerabilities = filteredMetricsBySeverity.filter(
    (v) => v.Count > 0,
  );
  const totalCount = validVulnerabilities.reduce((sum, v) => sum + v.Count, 0);
  return totalCount;
}

function getVulnMetricsAverageDaysOpenClose(
  sourceData: any,
  metricsType: MetricsType,
  severityFilters: any,
) {
  const filteredMetricsBySeverity = filterMetricsBySeverity(
    sourceData,
    metricsType,
    severityFilters,
  );

  const validVulnerabilities = filteredMetricsBySeverity.filter(
    (v) => v.Count > 0,
  );
  const totalDays = validVulnerabilities.reduce(
    (sum, v) => sum + v.Days * v.Count,
    0,
  );
  const totalCount = validVulnerabilities.reduce((sum, v) => sum + v.Count, 0);
  const averageDaysOpenClose = totalCount > 0 ? totalDays / totalCount : 0;
  return averageDaysOpenClose.toFixed(1);
}

function filterMetricsBySeverity(
  sourceData: any,
  metricsType: MetricsType,
  severityFilters: any,
) {
  const pcNode = sourceData[metricsType];
  const severities = ['Urgent', 'Critical', 'Medium', 'Minimal', 'Serious'];
  const resultArray: any[] = [];
  severities.forEach((severity) => {
    if (severityFilters.includes(severity)) {
      const internalValues = pcNode[severity];
      resultArray.push(internalValues);
    }
  });
  return resultArray;
}

function getVulnMetricsByTimeData(
  sourceData: any,
  metricsType: MetricsType,
  severityFilter: any,
) {
  const pcData: number[] = [];

  severityFilter.forEach((severity) => {
    const pcNode = sourceData[metricsType][severity];
    const pcValues: any[] = Object.values(pcNode);
    for (let i = 0; i < pcValues.length; i++) {
      if (pcData[i]) pcData[i] += pcValues[i];
      else pcData[i] = pcValues[i];
    }
  });
  const resultsArray: number[][] = [];
  if (checkNonZeroArray(pcData)) {
    resultsArray.push(pcData);
  }
  return resultsArray;
}

function getVulnMetricsTooltips(
  sourceData: any,
  metricsType: MetricsType,
  propertyName: string,
) {
  const pcNode = sourceData[metricsType];
  const pcValues: any[] = Object.values(pcNode);
  const pcData = pcValues.map((x) => x[propertyName]);

  const toolTipLabels: string[] = [];
  if (checkNonZeroArray(pcData)) {
    toolTipLabels.push('PC');
  }
  return toolTipLabels;
}
